import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Loader2, 
  User, 
  Building2, 
  CreditCard,
  Calendar,
  Stethoscope,
  MapPin,
  Phone,
  AlertCircle,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { setupDoctorProfile } from '@/lib/supabase/queries/auth';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from 'react-i18next';

interface ProfileFormData {
  // Personal Information
  fullName: string;
  
  // Professional Information
  medicalLicense: string;
  licenseExpiry: string;
  specialization: string;
  
  // Practice Information
  practiceName: string;
  practiceAddress: string;
  practicePhone: string;
  country: string;
}

export default function SetupProfilePage() {
  const { t } = useTranslation('doctor-platform');
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<ProfileFormData>({
    fullName: '',
    medicalLicense: '',
    licenseExpiry: '',
    specialization: '',
    practiceName: '',
    practiceAddress: '',
    practicePhone: '',
    country: 'Gambia',
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [userEmail, setUserEmail] = useState('');

  const checkAuth = useCallback(async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error || !user) {
        // Not authenticated, redirect to signup
        navigate('/auth/signup');
        return;
      }
      
      setUserEmail(user.email || '');
      
      // Check if user already has a profile
      const { data: existingProfile } = await supabase
        .from('doctor_profiles')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();
      
      if (existingProfile) {
        // Profile already exists, redirect to dashboard
        toast({
          title: t('auth.setupProfile.validation.profileExists'),
          description: t('auth.setupProfile.validation.redirecting'),
        });
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  }, [navigate, t, toast]);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateStep1 = () => {
    if (!formData.fullName.trim()) {
      setError(t('auth.setupProfile.validation.fullNameRequired'));
      return false;
    }
    if (!formData.medicalLicense.trim()) {
      setError(t('auth.setupProfile.validation.medicalLicenseRequired'));
      return false;
    }
    if (!formData.licenseExpiry) {
      setError(t('auth.setupProfile.validation.licenseExpiryRequired'));
      return false;
    }
    
    // Check if license expiry is in the future
    const expiryDate = new Date(formData.licenseExpiry);
    if (expiryDate < new Date()) {
      setError(t('auth.setupProfile.validation.licenseExpiryFuture'));
      return false;
    }
    
    return true;
  };

  const validateStep2 = () => {
    if (!formData.practiceName.trim()) {
      setError(t('auth.setupProfile.validation.practiceNameRequired'));
      return false;
    }
    if (!formData.practiceAddress.trim()) {
      setError(t('auth.setupProfile.validation.practiceAddressRequired'));
      return false;
    }
    if (!formData.country) {
      setError(t('auth.setupProfile.validation.countryRequired'));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!validateStep2()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Setup doctor profile
      const result = await setupDoctorProfile({
        fullName: formData.fullName,
        medicalLicense: formData.medicalLicense,
        licenseExpiry: formData.licenseExpiry,
        specialization: formData.specialization ? [formData.specialization] : undefined,
        practiceName: formData.practiceName,
        practiceAddress: formData.practiceAddress,
        practicePhone: formData.practicePhone,
        country: formData.country,
      });
      
      if (result.error) {
        throw result.error;
      }
      
      toast({
        title: t('auth.setupProfile.toast.title'),
        description: t('auth.setupProfile.toast.description'),
      });
      
      // Navigate to pending approval page
      navigate('/pending-approval');
    } catch (error: any) {
      console.error('Profile setup error:', error);
      setError(error.message || t('auth.setupProfile.validation.createFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Stethoscope className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {t('auth.setupProfile.title')}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {t('auth.setupProfile.stepLabel', {
                current: currentStep,
                step: currentStep === 1 ? t('auth.setupProfile.stepTitles.professional') : t('auth.setupProfile.stepTitles.practice'),
              })}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex space-x-2">
              <div className={`h-2 flex-1 rounded ${currentStep >= 1 ? 'bg-primary' : 'bg-gray-200'}`} />
              <div className={`h-2 flex-1 rounded ${currentStep >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />
            </div>
          </div>

          {/* Form Card */}
          <Card>
            <CardHeader>
              <CardTitle>
                {currentStep === 1 ? t('auth.setupProfile.stepTitles.professional') : t('auth.setupProfile.stepTitles.practice')}
              </CardTitle>
              <CardDescription>
                {currentStep === 1 
                  ? t('auth.setupProfile.descriptions.professional')
                  : t('auth.setupProfile.descriptions.practice')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {userEmail && (
                <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {t('auth.setupProfile.profileFor', { email: userEmail })}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {currentStep === 1 ? (
                  <>
                    {/* Step 1: Professional Information */}
                    <div>
                      <Label htmlFor="fullName">{t('auth.setupProfile.fullName')}</Label>
                      <div className="mt-1 relative">
                        <Input
                          id="fullName"
                          type="text"
                          value={formData.fullName}
                          onChange={(e) => handleInputChange('fullName', e.target.value)}
                          required
                          placeholder={t('auth.setupProfile.fullNamePlaceholder')}
                          className="pl-10"
                          disabled={isLoading}
                        />
                        <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="medicalLicense">{t('auth.setupProfile.medicalLicense')}</Label>
                      <div className="mt-1 relative">
                        <Input
                          id="medicalLicense"
                          type="text"
                          value={formData.medicalLicense}
                          onChange={(e) => handleInputChange('medicalLicense', e.target.value)}
                          required
                          placeholder={t('auth.setupProfile.medicalLicensePlaceholder')}
                          className="pl-10"
                          disabled={isLoading}
                        />
                        <CreditCard className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="licenseExpiry">{t('auth.setupProfile.licenseExpiry')}</Label>
                      <div className="mt-1 relative">
                        <Input
                          id="licenseExpiry"
                          type="date"
                          value={formData.licenseExpiry}
                          onChange={(e) => handleInputChange('licenseExpiry', e.target.value)}
                          required
                          className="pl-10"
                          disabled={isLoading}
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="specialization">{t('auth.setupProfile.specialization')}</Label>
                      <Input
                        id="specialization"
                        type="text"
                        value={formData.specialization}
                        onChange={(e) => handleInputChange('specialization', e.target.value)}
                        placeholder={t('auth.setupProfile.specializationPlaceholder')}
                        disabled={isLoading}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Step 2: Practice Details */}
                    <div>
                      <Label htmlFor="practiceName">{t('auth.setupProfile.practiceName')}</Label>
                      <div className="mt-1 relative">
                        <Input
                          id="practiceName"
                          type="text"
                          value={formData.practiceName}
                          onChange={(e) => handleInputChange('practiceName', e.target.value)}
                          required
                          placeholder={t('auth.setupProfile.practiceNamePlaceholder')}
                          className="pl-10"
                          disabled={isLoading}
                        />
                        <Building2 className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="practiceAddress">{t('auth.setupProfile.practiceAddress')}</Label>
                      <div className="mt-1 relative">
                        <Textarea
                          id="practiceAddress"
                          value={formData.practiceAddress}
                          onChange={(e) => handleInputChange('practiceAddress', e.target.value)}
                          required
                          placeholder={t('auth.setupProfile.practiceAddressPlaceholder')}
                          rows={3}
                          disabled={isLoading}
                          className="pl-10 pt-2"
                        />
                        <MapPin className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="practicePhone">{t('auth.setupProfile.practicePhone')}</Label>
                      <div className="mt-1 relative">
                        <Input
                          id="practicePhone"
                          type="tel"
                          value={formData.practicePhone}
                          onChange={(e) => handleInputChange('practicePhone', e.target.value)}
                          placeholder="+220 000 0000"
                          className="pl-10"
                          disabled={isLoading}
                        />
                        <Phone className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="country">{t('auth.setupProfile.country')}</Label>
                      <select
                        id="country"
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        disabled={isLoading}
                        required
                      >
                        <option value="Gambia">{t('auth.setupProfile.countries.gambia')}</option>
                        <option value="Senegal">{t('auth.setupProfile.countries.senegal')}</option>
                        <option value="Cameroon">{t('auth.setupProfile.countries.cameroon')}</option>
                        <option value="USA">{t('auth.setupProfile.countries.usa')}</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="flex justify-between pt-4">
                  {currentStep === 2 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      disabled={isLoading}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      {t('auth.setupProfile.back')}
                    </Button>
                  )}
                  
                  {currentStep === 1 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      className="ml-auto"
                      disabled={isLoading}
                    >
                      {t('auth.setupProfile.next')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="ml-auto"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('auth.setupProfile.submitting')}
                        </>
                      ) : (
                        <>
                          {t('auth.setupProfile.submit')}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Help Text */}
          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            <p>
              {t('auth.setupProfile.footer')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
